import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb, 
  faFireFlameCurved, 
  faWifi, 
  faArrowRight, 
  faShieldHalved, 
  faPiggyBank, 
  faHeadset,
  faChartLine,
  faCheckCircle,
  faBolt,
  faStar,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import CallToAction from '../components/CallToAction';
import LogoSlider from '../components/LogoSlider';
import { fornitori } from '../utils/data';

const Home: React.FC = () => {
  const [activeService, setActiveService] = useState<'luce' | 'gas' | 'internet'>('luce');
  const [consumo, setConsumo] = useState<string>('');
  const [persone, setPersone] = useState<string>('3-4');
  const [risparmio, setRisparmio] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  const calcolaRisparmio = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!consumo || parseFloat(consumo) <= 0) {
      alert('Inserisci un consumo valido');
      return;
    }

    const consumoNum = parseFloat(consumo);
    let risparmioCalcolato = 0;

    // Logica di calcolo basata sul tipo di servizio
    if (activeService === 'luce') {
      // Calcolo per luce: risparmio medio del 25-35% sul costo attuale
      // Assumiamo un costo medio di 0.30€/kWh per il mercato tutelato
      const costoMedioKwh = 0.30;
      const costoAttuale = consumoNum * costoMedioKwh * 12; // Annuale
      const percentualeRisparmio = persone === '5+' ? 0.35 : persone === '3-4' ? 0.30 : 0.25;
      risparmioCalcolato = costoAttuale * percentualeRisparmio;
    } else if (activeService === 'gas') {
      // Calcolo per gas: risparmio medio del 20-30%
      // Assumiamo un costo medio di 1.20€/Smc
      const costoMedioSmc = 1.20;
      const costoAttuale = consumoNum * costoMedioSmc * 12; // Annuale
      const percentualeRisparmio = persone === '5+' ? 0.30 : persone === '3-4' ? 0.25 : 0.20;
      risparmioCalcolato = costoAttuale * percentualeRisparmio;
    } else if (activeService === 'internet') {
      // Calcolo per internet: risparmio basato sul costo mensile
      // Assumiamo un risparmio del 20% rispetto al costo attuale
      const costoAnnuale = consumoNum * 12;
      const percentualeRisparmio = 0.20;
      risparmioCalcolato = costoAnnuale * percentualeRisparmio;
    }

    setRisparmio(Math.round(risparmioCalcolato));
    setShowResult(true);
  };

  const resetCalcolatore = () => {
    setConsumo('');
    setPersone('3-4');
    setRisparmio(null);
    setShowResult(false);
  };

  const stats = [
    { value: '15.000+', label: 'Clienti Soddisfatti', icon: faCheckCircle },
    { value: '50+', label: 'Partner Energetici', icon: faBolt },
    { value: '10', label: 'Anni di Esperienza', icon: faChartLine },
    { value: '98%', label: 'Tasso di Soddisfazione', icon: faStar },
  ];


  return (
    <div className="overflow-hidden">
      {/* Hero Section - Split Design */}
      <section className="relative pt-24 md:pt-20 pb-32 bg-gradient-to-br from-primary via-primary to-secondary overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[600px]">
            {/* Left Side - Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
              >
                <span className="text-sm font-semibold">✨ Il tuo partner energetico di fiducia</span>
              </motion.div>

              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Risparmia fino al
                <span className="block text-accent text-6xl md:text-7xl mt-2">40%</span>
                sulle bollette
              </h1>
              
              <p className="text-xl mb-8 text-white/90 leading-relaxed">
                Confronta le migliori offerte di luce, gas e internet. 
                <span className="font-semibold text-accent"> Gratis e senza impegno.</span>
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/offerte" className="btn bg-accent text-white hover:bg-accent/90 text-lg px-8 py-4 shadow-2xl hover:shadow-accent/50 transition-all">
                  <FontAwesomeIcon icon={faBolt} className="mr-2" /> Inizia Ora
                </Link>
                <a href="tel:+390240055045" className="btn bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-4">
                  <FontAwesomeIcon icon={faHeadset} className="mr-2" /> Chiamaci
                </a>
              </div>

              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-accent" />
                  <span>Confronto gratuito</span>
                </div>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-accent" />
                  <span>Nessun costo nascosto</span>
                </div>
              </div>
            </motion.div>

            {/* Right Side - Interactive Card */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-3xl shadow-2xl p-8 backdrop-blur-sm">
                <div className="flex gap-4 mb-6">
                  {(['luce', 'gas', 'internet'] as const).map((service) => (
                    <button
                      key={service}
                      onClick={() => setActiveService(service)}
                      className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                        activeService === service
                          ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {service.charAt(0).toUpperCase() + service.slice(1)}
                    </button>
                  ))}
                </div>

                <motion.div
                  key={activeService}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {!showResult ? (
                    <form onSubmit={calcolaRisparmio} className="space-y-4">
                      <h3 className="text-2xl font-bold text-gray-800">
                        Calcola il tuo risparmio su {activeService}
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {activeService === 'internet' 
                              ? 'Costo attuale mensile (€)' 
                              : `Consumo mensile medio (${activeService === 'luce' ? 'kWh' : 'Smc'})`
                            }
                          </label>
                          <input 
                            type="number" 
                            value={consumo}
                            onChange={(e) => setConsumo(e.target.value)}
                            className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors bg-white text-gray-900" 
                            placeholder={activeService === 'internet' ? 'Es. 35' : activeService === 'luce' ? 'Es. 250' : 'Es. 120'}
                            required
                            min="1"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Numero di persone nel nucleo familiare
                          </label>
                          <select 
                            value={persone}
                            onChange={(e) => setPersone(e.target.value)}
                            className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors bg-white text-gray-900"
                          >
                            <option value="1-2">1-2 persone</option>
                            <option value="3-4">3-4 persone</option>
                            <option value="5+">5+ persone</option>
                          </select>
                        </div>

                        <button 
                          type="submit"
                          className="w-full btn bg-gradient-to-r from-primary to-secondary text-white py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                        >
                          <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                          Calcola Risparmio
                        </button>
                      </div>

                      <div className="mt-6 p-4 bg-gradient-to-r from-accent/10 to-secondary/10 rounded-xl border-2 border-accent/20">
                        <p className="text-sm text-gray-600 text-center">
                          💡 <span className="font-semibold">In media i nostri clienti risparmiano €350/anno</span>
                        </p>
                      </div>
                    </form>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="space-y-6"
                    >
                      <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mb-4">
                          <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">
                          Potenziale di Risparmio
                        </h3>
                      </div>

                      <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-2xl p-8 text-center">
                        <p className="text-sm opacity-90 mb-2">Risparmio Stimato</p>
                        <div className="text-5xl md:text-6xl font-bold mb-2">
                          €{risparmio}/anno
                        </div>
                        <p className="text-sm opacity-90">con le nostre offerte personalizzate</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center">
                          <p className="text-sm text-green-700 mb-1">Risparmio mensile</p>
                          <p className="text-2xl font-bold text-green-800">
                            €{Math.round((risparmio || 0) / 12)}
                          </p>
                        </div>
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-center">
                          <p className="text-sm text-blue-700 mb-1">Tempo di risparmio</p>
                          <p className="text-2xl font-bold text-blue-800">
                            Immediato
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Link 
                          to="/offerte"
                          className="flex-1 btn bg-gradient-to-r from-primary to-secondary text-white py-3 font-semibold text-center"
                        >
                          Vedi Offerte
                        </Link>
                        <button
                          onClick={resetCalcolatore}
                          className="flex-1 btn bg-gray-100 text-gray-700 hover:bg-gray-200 py-3 font-semibold"
                        >
                          Ricalcola
                        </button>
                      </div>

                      <a 
                        href="tel:+390660513535"
                        className="block text-center text-primary hover:text-secondary font-semibold"
                      >
                        <FontAwesomeIcon icon={faPhone} className="mr-2" />
                        Chiamaci per una consulenza gratuita
                      </a>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" className="w-full h-auto">
            <path fill="#F9FAFB" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-light-background">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl mb-4 shadow-lg">
                  <FontAwesomeIcon icon={stat.icon} className="text-2xl text-white" />
                </div>
                <div className="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - New Grid Layout */}
      <section className="py-20 bg-white">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                I Nostri Servizi
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Soluzioni complete per tutte le tue esigenze energetiche
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: faLightbulb,
                title: 'Offerte Luce',
                description: 'Tariffe competitive e energia 100% verde. Risparmia fino al 40% sulla bolletta.',
                features: ['Prezzo bloccato', 'Energia verde', 'Attivazione rapida'],
                color: 'from-yellow-400 to-orange-500'
              },
              {
                icon: faFireFlameCurved,
                title: 'Offerte Gas',
                description: 'Riscaldamento conveniente per la tua casa con tariffe trasparenti.',
                features: ['Tariffe fisse', 'Nessun vincolo', 'Assistenza 24/7'],
                color: 'from-red-400 to-pink-500'
              },
              {
                icon: faWifi,
                title: 'Offerte Internet',
                description: 'Fibra ultraveloce fino a 2.5 Gbps. Naviga senza limiti.',
                features: ['Fibra FTTH', 'Modem incluso', 'Wi-Fi 6'],
                color: 'from-blue-400 to-cyan-500'
              }
            ].map((service, index) => (
            <motion.div 
                key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-transparent overflow-hidden"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                
                <div className="relative z-10">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                    <FontAwesomeIcon icon={service.icon} className="text-2xl text-white" />
                </div>
                  
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-primary mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/offerte" className="inline-flex items-center text-primary font-semibold group-hover:text-secondary transition-colors">
                    Scopri di più <FontAwesomeIcon icon={faArrowRight} className="ml-2 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Redesigned */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Perché Sceglierci
              </span>
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: faPiggyBank,
                title: 'Risparmio Garantito',
                description: 'Analizziamo i tuoi consumi e troviamo l\'offerta più conveniente. In media i clienti risparmiano €350/anno.',
                gradient: 'from-green-400 to-emerald-500'
              },
              {
                icon: faShieldHalved,
                title: 'Massima Trasparenza',
                description: 'Nessun costo nascosto. Contratti chiari e assistenza continua per ogni tua necessità.',
                gradient: 'from-blue-400 to-indigo-500'
              },
              {
                icon: faHeadset,
                title: 'Supporto Dedicato',
                description: 'Team di esperti sempre a tua disposizione. Risposta entro 2 ore e assistenza anche nei festivi.',
                gradient: 'from-purple-400 to-pink-500'
              }
            ].map((item, index) => (
          <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 rounded-2xl hover:bg-white hover:shadow-xl transition-all duration-300"
              >
                <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-3xl mb-6 shadow-lg`}>
                  <FontAwesomeIcon icon={item.icon} className="text-3xl text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-800">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
          </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-800">
              Partner di Fiducia
            </h2>
            <p className="text-lg text-gray-600">
              Collaboriamo con i migliori fornitori energetici
              </p>
            </motion.div>

          <div className="bg-white py-8 px-4 rounded-3xl shadow-xl">
            <LogoSlider logos={fornitori} speed={40} className="py-4" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
          <CallToAction 
              title="Pronto a Risparmiare?" 
              subtitle="Inizia ora e scopri quanto puoi risparmiare sulle tue bollette. Consulenza gratuita!"
            variant="primary"
              className="shadow-2xl"
          />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 
